import React from 'react';

import { useIntl } from 'react-intl';

import { useCreateComponentDeviceConfig } from 'src/features/systems/api/systemComponents/useCreateComponentDeviceConfig';
import { ComponentDeviceConfig, DeviceConfigField } from 'src/features/systems/utils/deviceConfigFields';
import { systemsMessages } from 'src/features/systems/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { InputWithCopy } from 'src/registry/components/inputs/InputWithCopy';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { Label } from 'src/registry/ui/Label';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

type Props = {
    componentId: string;
    fields: DeviceConfigField[];
    /** Whether opening this dialog rotates the component's API key, which the user is warned about. */
    rotatesApiKey: boolean;
    children?: React.ReactNode;
};

/**
 * Shows the values an installer has to type into the device's own portal.
 *
 * The values are fetched with a POST fired once when the dialog opens, not with a query: the call
 * rotates the component's API key, so an automatic refetch (on window focus, say, when the
 * installer alt-tabs back from the camera page) would invalidate the key they had just pasted in.
 * Holding the response in local state means the only way to rotate is to reopen the dialog.
 */
export default function DeviceConfigModal({ componentId, fields, rotatesApiKey, children }: Props) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
    const [config, setConfig] = React.useState<ComponentDeviceConfig | null>(null);

    const { mutate, isPending } = useCreateComponentDeviceConfig({
        onSuccess: (response) => setConfig(response),
    });

    // Fetching is separated from opening because the two are triggered differently: a gateway
    // controller opens the dialog straight from its trigger, while a camera must pass through a
    // confirmation first (see below) and is opened programmatically.
    const fetchConfig = React.useCallback(() => {
        setConfig(null);
        mutate({ parkingId, componentId });
    }, [componentId, mutate, parkingId]);

    const handleOpenChange = React.useCallback(
        (open: boolean) => {
            setIsModalOpen(open);

            if (open) fetchConfig();
            else setConfig(null);
        },
        [fetchConfig],
    );

    // Confirming is what actually mints the key, so the request fires here rather than on the
    // trigger. setIsModalOpen(true) does not re-enter handleOpenChange — Radix only calls that for
    // interactions it initiates — so the config is fetched exactly once.
    const handleConfirm = React.useCallback(() => {
        setIsConfirmOpen(false);
        fetchConfig();
        setIsModalOpen(true);
    }, [fetchConfig]);

    return (
        <>
            {/* A camera's config mints a new key and kills the old one, so a misclick can take a
                working camera offline — it must be confirmed first. A gateway controller mints
                nothing, so making its operator confirm would be noise. */}
            {rotatesApiKey ? (
                <ConfirmationModal
                    isModalOpen={isConfirmOpen}
                    setIsModalOpen={setIsConfirmOpen}
                    dialogDetails={{
                        title: systemsMessages.deviceConfigurationConfirmTitle,
                        confirmationMessage: systemsMessages.deviceConfigurationConfirmMessage,
                        icon: ICON_MAP.DANGER,
                    }}
                    callbackButton={{
                        variant: 'default',
                        buttonTitle: systemsMessages.deviceConfigurationConfirmAction,
                        callbackFn: handleConfirm,
                    }}
                >
                    {children}
                </ConfirmationModal>
            ) : null}
            <Dialog open={isModalOpen} onOpenChange={handleOpenChange}>
                {!rotatesApiKey && <DialogTrigger asChild>{children}</DialogTrigger>}
                <DialogContent>
                    <DialogHeader>
                        <div className="flex gap-2">
                            <SquareIcon icon={ICON_MAP.COG} />
                            <div>
                                <DialogTitle>{formatMessage(systemsMessages.deviceConfiguration)}</DialogTitle>
                                <DialogDescription>
                                    {formatMessage(systemsMessages.deviceConfigurationDescription)}
                                </DialogDescription>
                            </div>
                        </div>
                    </DialogHeader>
                    <DialogSeparator />
                    <DialogBody>
                        <div className="flex flex-col gap-4">
                            {fields.map((field) => (
                                <div key={field.key} className="flex flex-col gap-1">
                                    {/* Not translated on purpose - this is the device's own label. */}
                                    <Label>{field.label}</Label>
                                    {isPending || !config ? (
                                        <Skeleton className="h-9 w-full" />
                                    ) : (
                                        <InputWithCopy value={config[field.key] ?? ''} />
                                    )}
                                    {field.note && <span className="text-xs text-gray-500">{field.note}</span>}
                                </div>
                            ))}
                            {rotatesApiKey && (
                                <p className="text-xs text-amber-700">
                                    {formatMessage(systemsMessages.deviceConfigurationKeyWarning)}
                                </p>
                            )}
                        </div>
                    </DialogBody>
                    <DialogSeparator />
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button type="button" variant="outline" className="flex-1">
                                {formatMessage(generalMessages.close)}
                            </Button>
                        </DialogClose>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
